import { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
} from "@mui/material";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import PageHeader from "../../components/common/PageHeader";
import PageActions from "../../components/common/PageActions";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DataTableWrapper from "../../components/common/DataTableWrapper";
import PaginationBar from "../../components/common/PaginationBar";

import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import {
  useListTransfersQuery,
  useSetTransferStatusMutation,
  useExportTransfersXlsxMutation,
} from "../../features/transfers/transfersApi";
import type { TransferStatus } from "../../features/transfers/types";

import { useListProvidersQuery, useListClientsQuery } from "../../features/masterdata/masterdataApi";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";
import { getTodayString } from "../../utils/date";

function statusChip(status: TransferStatus) {
  const label =
    status === "planned" ? "Planned" :
    status === "in_progress" ? "In progress" :
    status === "completed" ? "Completed" : "Cancelled";
  return <Chip size="small" label={label} />;
}

export default function TransfersListPage() {
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  type Ordering = "service_date" | "-service_date" | "start_time" | "-start_time" | "created_at" | "-created_at";
  const [filters, setFilters] = usePersistedListFilters("transfers", {
    dateFrom: getTodayString(),
    dateTo: getTodayString(),
    statusFilter: "" as TransferStatus | "",
    provider: "",
    client: "",
    search: "",
    ordering: "service_date" as Ordering,
  });
  const { dateFrom, dateTo, statusFilter, provider, client, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const toggleSort = (field: "service_date" | "start_time" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as Ordering,
    }));
  };
  const sortIcon = (field: "service_date" | "start_time" | "created_at") => {
    if (ordering === field) return <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.25 }} />;
    if (ordering === `-${field}`) return <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.25 }} />;
    return <UnfoldMoreIcon sx={{ fontSize: 16, ml: 0.25, opacity: 0.5 }} />;
  };
  const [page, setPage] = useState(1);

   // actions (delete not supported by backend; confirm shows info message)
  const [setStatus, { isLoading: isSettingStatus }] = useSetTransferStatusMutation();
  const [exportTransfersXlsx, { isLoading: isExporting }] = useExportTransfersXlsxMutation();

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, statusFilter, provider, client, debouncedSearch, ordering]);

  // dropdown data (MVP: bring first 200)
  const providersQ = useListProvidersQuery({ page: 1, page_size: 200, ordering: "name" });
  const clientsQ = useListClientsQuery({ page: 1, page_size: 200, ordering: "name" });

  const { data, isLoading, isFetching, error, refetch } = useListTransfersQuery({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    status: statusFilter || undefined,
    provider: provider || undefined,
    client: client || undefined,
    search: debouncedSearch,
    ordering,
    page,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  // Resolve provider/client UUIDs to names for display (API returns IDs)
  const providerNames = useMemo(() => {
    const map: Record<string, string> = {};
    (providersQ.data?.results ?? []).forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [providersQ.data?.results]);
  const clientNames = useMemo(() => {
    const map: Record<string, string> = {};
    (clientsQ.data?.results ?? []).forEach((c) => {
      map[c.id] = c.name;
    });
    return map;
  }, [clientsQ.data?.results]);

 
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string>("");

  const onConfirmDelete = async () => {
    if (!deleteId) return;
    setOpenDelete(false);
    setDeleteId(null);
    setDeleteName("");
    enqueueSnackbar("Deleting transfers is not supported. Use status or archive instead.", { variant: "info" });
  };

  const onQuickStatus = async (id: string, next: TransferStatus) => {
    try {
      await setStatus({ id, status: next }).unwrap();
      enqueueSnackbar(`Status updated to ${next}`, { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed to update status", { variant: "error" });
    }
  };

  const onStartExport = () => {
    exportTransfersXlsx({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      status: statusFilter || undefined,
      provider: provider || undefined,
      client: client || undefined,
      search: debouncedSearch || undefined,
      ordering,
    })
      .unwrap()
      .then(() => enqueueSnackbar("Export downloaded", { variant: "success" }))
      .catch((e: any) =>
        enqueueSnackbar(e?.data?.detail || "Export failed", { variant: "error" })
      );
  };

  const showEmpty = !isLoading && rows.length === 0;

  return (
    <Box>
      <PageHeader
        title="Transfers"
        subtitle="Plan and track daily routes"
        actions={
          <PageActions
            onStartExport={onStartExport}
            exportDisabled={isExporting}
          >
            <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => nav("/app/transfers/new")}>
              New Transfer
            </Button>
          </PageActions>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Date from"
          type="date"
          value={dateFrom}
          onChange={(e) => setFilters({ dateFrom: e.target.value })}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Date to"
          type="date"
          value={dateTo}
          onChange={(e) => setFilters({ dateTo: e.target.value })}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Status"
          select
          value={statusFilter}
          onChange={(e) => setFilters({ statusFilter: e.target.value as TransferStatus | "" })}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="planned">Planned</MenuItem>
          <MenuItem value="in_progress">In progress</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        <TextField
          label="Provider"
          select
          value={provider}
          onChange={(e) => setFilters({ provider: e.target.value })}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {(providersQ.data?.results ?? []).map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Client"
          select
          value={client}
          onChange={(e) => setFilters({ client: e.target.value })}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {(clientsQ.data?.results ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search (description, notes)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 200, flex: 1 }}
        />
      </FilterBar>

      {showEmpty ? (
        <EmptyState
          title="No transfers yet"
          description="Create your first transfer."
          actionLabel="Create transfer"
          onAction={() => nav("/app/transfers/new")}
        />
      ) : (
        <>
          <DataTableWrapper
            loading={isLoading}
            error={!!error}
            errorMessage="Failed to load transfers."
            onRetry={() => refetch()}
            minWidth={720}
          >
            <Table sx={{ minWidth: 720 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box
                      component="span"
                      onClick={() => toggleSort("service_date")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Date{sortIcon("service_date")}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      onClick={() => toggleSort("start_time")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Time{sortIcon("start_time")}
                    </Box>
                  </TableCell>
                  <TableCell>Description / Route</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.service_date}</TableCell>
                    <TableCell>{r.start_time}</TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 600 }}>{r.description || "—"}</Box>
                    </TableCell>
                    <TableCell>{r.provider ? (providerNames[r.provider] ?? r.provider) : "—"}</TableCell>
                    <TableCell>{r.client ? (clientNames[r.client] ?? r.client) : "—"}</TableCell>
                    <TableCell>{statusChip(r.status)}</TableCell>
                    <TableCell align="right">
                      {r.price_to_provider != null ? `€${r.price_to_provider}` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Mark completed">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onQuickStatus(r.id, "completed")}
                            disabled={isSettingStatus || r.status === "completed"}
                            aria-label="Mark completed"
                          >
                            <CheckCircleOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel transfer">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onQuickStatus(r.id, "cancelled")}
                            disabled={isSettingStatus || r.status === "cancelled"}
                            aria-label="Cancel transfer"
                          >
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => nav(`/app/transfers/${r.id}/edit`)}
                          aria-label="Edit transfer"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataTableWrapper>

          <PaginationBar
            count={count}
            page={page}
            hasPrev={canPrev}
            hasNext={!!canNext}
            disabled={isFetching}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}

      <ConfirmDialog
        open={openDelete}
        title="Delete transfer?"
        description={deleteId ? `This will delete "${deleteName}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={false}
        onClose={() => {
          setOpenDelete(false);
          setDeleteId(null);
        }}
        onConfirm={onConfirmDelete}
      />
    </Box>
  );
}
